import * as _ from 'lodash';
import { useState } from 'react';
import { Button, Modal } from 'react-bootstrap';
import DropZone from 'react-dropzone';
import { useDispatch, useSelector } from 'react-redux';
import type { ProgressEvent } from 'superagent';

import { REQUIRE_TEAM_ON_UPLOAD } from '../../../../shared/featureFlags/flagNames';
import { FileType } from '../../../../shared/fileUpload';
import * as promises from '../../../../shared/promises';
import { getSignedUrl, triggerBuilds, uploadFile } from '../../../api/componentUploader';
import { useRouter } from '../../../contexts/RouterContext';
import { window } from '../../../globals';
import { errorMessageFromResponse } from '../../../helpers/errors';
import { useIsFeatureEnabled } from '../../../helpers/features/featureFlags';
import { userHasPermissionToViewAnyProject } from '../../../helpers/permissions';
import useOnMount from '../../../hooks/useOnMount';
import TeamsModule from '../../../redux/teams';
import {
  archiveUploadAccept,
  codeSentryArchiveUploadAccept,
  nuc,
  sbomUploadAccept,
  validArchiveUploadExtensions,
  validCodeSentryArchiveUploadExtensions,
  validSBOMUploadExtensions,
} from '../../../utils';
import Icon, { icons, IconText } from '../../generic/Icon';
import Subnav from '../../generic/Subnav';
import SelectTeam, { teamsUserCanAddProject } from '../SelectTeam';
import type { Team } from '../../../../shared/model-typings/team';
import type { FailedFetchResponse } from '../../../helpers/errors';
import type { ReduxStore } from '../../../redux/store';
import type { FileUploadMetadata } from '../../../types';

import UploadComponentHeader from './UploadComponentHeader';
import UploadComponentTable from './UploadComponentTable';

import styles from './UploadComponentPage.module.less';

export type FileToUpload = File & {
  customName?: string;
  revision?: string;
  // needs to be cleaned up on unmount
  preview?: string;
};

export type FileFieldUpdateDelegate = (field: 'customName' | 'revision', value: string, file: FileToUpload) => void;

type Props = {
  fileType: FileType;
};

type FileStatus = {
  bytesUploaded: number;
  isSuccessfullyUploaded: boolean;
  errorMessage?: string;
};

export type FileToUploadAndStatus = { file: FileToUpload; status: FileStatus };

type UploadComponentPageState = {
  files: Record<string, FileToUploadAndStatus>;
  assignToTeams: Team[];
  showAssignToTeamsModal: boolean;
  isUploading: boolean;
};

function getUploadTitle(fileType: FileType): string {
  if (fileType === FileType.Binary) {
    return 'Binary';
  }
  if (fileType === FileType.SBOM) {
    return 'SBOM';
  }
  return 'Archive';
}

function getFetcherPrefix(fileType: FileType): string {
  if (fileType === FileType.Binary) {
    return 'binary';
  }
  if (fileType === FileType.SBOM) {
    return 'sbom';
  }
  return 'archive';
}

const UploadComponentsPage = ({ fileType }: Props) => {
  const { push } = useRouter();
  const dispatch = useDispatch();
  const allTeams = useSelector((state: ReduxStore) => TeamsModule.selectors.getTeamList(state));
  const [state, setState] = useState<UploadComponentPageState>({
    files: {},
    assignToTeams: [],
    showAssignToTeamsModal: false,
    isUploading: false,
  });

  const getFiles = (): FileToUpload[] => {
    return Object.values(state.files).map(f => f.file);
  };

  useOnMount(() => {
    if (window.user) {
      dispatch(TeamsModule.actions.fetchAll({ organizationId: window.user.organizationId }));
    }
    return () => {
      // prevents memory leaks because DropZone does not manage dropped files
      getFiles().forEach(file => {
        if (file.preview) {
          URL.revokeObjectURL(file.preview);
        }
      });
    };
  });

  const isTeamRequired = useIsFeatureEnabled(REQUIRE_TEAM_ON_UPLOAD);

  // Determine file extensions and accept types based on fileType
  let validFileExtensions: readonly string[] = [];
  let acceptFileTypes: Record<string, string[]> = {};
  let fetcher: 'sbom' | 'archive' | 'binary';

  if (fileType === FileType.Binary) {
    // Only binary file type uses CodeSentry extensions
    validFileExtensions = validCodeSentryArchiveUploadExtensions;
    acceptFileTypes = codeSentryArchiveUploadAccept;
    fetcher = 'binary';
  } else if (fileType === FileType.Archive) {
    // Archive always uses regular archive extensions
    validFileExtensions = validArchiveUploadExtensions;
    acceptFileTypes = archiveUploadAccept;
    fetcher = 'archive';
  } else {
    // Default to SBOM
    validFileExtensions = validSBOMUploadExtensions;
    acceptFileTypes = sbomUploadAccept;
    fetcher = 'sbom';
  }

  let backToServicesURL = '/projects/import';
  // Unlike SBOM and binary imports, the archive upload page is embedded within the quick import page,
  // so pressing the back button should return the user to the quick import page.
  if (fileType === FileType.Archive) {
    backToServicesURL = '/projects/import/cloud';
  }

  // get list of files that have not been successfully uploaded
  const getNonUploadedFiles = (): FileToUpload[] => {
    return Object.values(state.files).filter(f => !f.status.isSuccessfullyUploaded).map(f => f.file);
  };

  const onProgressUpdate = (file: FileToUpload, progress: ProgressEvent) => {
    if (!state.files[file.name]) {
      return;
    }

    setState(prevState => {
      const nextFileToUpload = (prevState.files[file.name] as FileToUploadAndStatus);
      nextFileToUpload.status = {
        bytesUploaded: progress.loaded,
        errorMessage: undefined,
        isSuccessfullyUploaded: false,
      };
      return {
        ...prevState,
        files: {
          ...prevState.files,
          [file.name]: nextFileToUpload,
        },
      };
    });
  };

  const applyCustomName = (file: FileToUpload) => {
    // eslint-disable-next-line no-param-reassign
    file.customName = parsePackageNameFromFileName(file.name);
    return file;
  };

  const onDrop = async (acceptedFiles: File[]) => {
    if (!acceptedFiles.length) {
      return;
    }

    setState(prevState => {
      const nextFiles = _.cloneDeep(prevState.files);
      acceptedFiles.forEach((file: FileToUpload) => {
        nextFiles[file.name] = {
          file: applyCustomName(file),
          status: {
            bytesUploaded: 0,
            errorMessage: undefined,
            isSuccessfullyUploaded: false,
          },
        };
      });

      return {
        ...prevState,
        files: nextFiles,
      };
    });
  };

  const parsePackageNameFromFileName = (fileName: string): string => {
    return validFileExtensions.reduce((simplifiedFileName: string, extension: string) =>
      simplifiedFileName.split(extension)[0] as string, fileName);
  };

  const promptForTeamAssignment = () => {
    // If the user has no teams to assign, just continue without prompting
    if (teamsUserCanAddProject(allTeams).length === 0) {
      uploadFiles();
      return;
    }

    // Prompt for assignment to teams
    setState(prevState => ({ ...prevState, showAssignToTeamsModal: true, assignToTeams: [] }));
  };

  const teamAssignmentClosed = () => {
    setState(prevState => ({ ...prevState, showAssignToTeamsModal: false }));
  };

  const uploadFiles = async (): Promise<void> => {
    const files = getNonUploadedFiles();
    // Set revision to timestamp if not provided
    files.forEach(file => {
      if (_.isEmpty(file.customName)) {
        throw new Error('All selected files must have a name');
      }
    });

    setState(prevState => ({ ...prevState, isUploading: true }));

    // If no revision provided, we default to the current timestamp. We don't set the field directly
    // on the file however to reduce confusion (i.e.: how did this field get set?)
    const defaultRevision = Date.now().toString();
    // handle errors on a file by file basis, so partial successes become possible
    await promises.map(files, async (file) => {
      const fileRevision = file.revision || defaultRevision;
      try {
        const uploadUrl = await getSignedUrl(file.customName as string, fileRevision, fileType);
        // upload file to S3/Minio
        await uploadFile(file, uploadUrl, (progress: ProgressEvent) => {
          onProgressUpdate(file, progress);
        });
        // Create Build in FOSSA backend
        await triggerBuilds(
          {
            archives: [
              {
                packageSpec: file.customName,
                revision: fileRevision,
                fileName: file.name,
                fileType,
              } as FileUploadMetadata,
            ],
            selectedTeams: state.assignToTeams.map((team) => _.pick(team, 'id', 'name', 'organizationId')),
          },
          { fileType },
        );
        // set file as "done" uploading
        setState(nextState => {
          const nextFile = nextState.files[file.name] as FileToUploadAndStatus;
          nextFile.status.isSuccessfullyUploaded = true;
          return {
            ...nextState,
            files: {
              ...nextState.files,
              [file.name]: nextFile,
            },
          };
        });
      } catch (error) {
        setState(nextState => {
          const nextFile = nextState.files[file.name] as FileToUploadAndStatus;
          nextFile.status.errorMessage = errorMessageFromResponse(error as FailedFetchResponse);
          return {
            ...nextState,
            files: {
              ...nextState.files,
              [file.name]: nextFile,
            },
          };
        });
      }
    }, { concurrency: 3 });

    // redirect to the project page if all the files were uploaded successfully
    if (getNonUploadedFiles().length === 0) {
      //  If there was only a single project uploaded, we can redirect to that project instead of the projects dashboard.
      if (files.length === 1) {
        //  If they are uploading a single SBOM file, it's more helpful to the
        //  user to be redirected to the actual project page
        if (fileType === FileType.SBOM || fileType === FileType.Archive || fileType === FileType.Binary) {
          const projectName = files?.[0]?.customName;
          const revision = files?.[0]?.revision ?? defaultRevision;
          if (projectName && revision) {
            const fetcherPrefix = getFetcherPrefix(fileType);
            //  We can infer the project locator now, from the customName.
            const projectLocator = nuc(`${fetcherPrefix}+${window.user?.organizationId}/${projectName}`);
            //  Note that this assumes that the default branch is called "master".
            //  This is safe because when SBOMs are imported, we use `master` as the default branch anyway.
            return push(`/projects/${projectLocator}/refs/branch/master/${nuc(revision)}`);
          }
        }
      }

      return push('/projects');
    }

    setState(prevState => ({ ...prevState, isUploading: false }));
  };

  const onTeamsSelected = (teams: Team[]) => {
    setState(prevState => ({ ...prevState, assignToTeams: teams }));
  };

  const onTeamsSelectionImport = () => {
    teamAssignmentClosed();
    uploadFiles();
  };

  const onRemoveFile = (fileName: string) => {
    if (!state.files[fileName]) {
      return;
    }

    setState(nextState => {
      const newFiles = _.cloneDeep(nextState.files);
      delete newFiles[fileName];
      return {
        ...nextState,
        files: newFiles,
      };
    });
  };

  const onUpdateFileField = (field: 'customName' | 'revision', value: string, file: FileToUpload) => {
    if (!state.files[file.name]) {
      return;
    }

    setState((prevState: UploadComponentPageState) => {
      const nextFile = (prevState.files[file.name] as FileToUploadAndStatus).file;
      nextFile[field] = value;
      return {
        ...prevState,
        files: {
          ...prevState.files,
          [file.name]: {
            file: nextFile,
            status: { bytesUploaded: 0, errorMessage: undefined, isSuccessfullyUploaded: false },
          },
        },
      };
    });
  };

  const allFiles = getFiles();
  const allNonUploadedFiles = getNonUploadedFiles();
  const hasFilesSelected = !!allFiles.length;

  return (
    <div>
      {/* don't give an easy way to navigate away from the page when upload has started */}
      {!state.isUploading && (
        <Subnav>
          <Subnav.Item to={backToServicesURL}>
            <IconText
              icon={icons.arrowLeft}
              text="Back to Services"
            />
          </Subnav.Item>
        </Subnav>
      )}
      <div className="container">
        <h1 className={`${styles.heading} text-left margin-bottom-xl`}>
          {getUploadTitle(fileType)} Upload
        </h1>
        <div className={`${styles.container} card`}>
          <div className={styles.header}>
            <UploadComponentHeader
              numUploads={allNonUploadedFiles.length}
              onDropAdditionalFiles={onDrop}
              acceptedFileTypes={acceptFileTypes}
              onUpload={promptForTeamAssignment}
              isUploadEnabled={allNonUploadedFiles.length > 0 && !state.isUploading}
            />
          </div>
          {hasFilesSelected && (
            <UploadComponentTable
              files={Object.values(state.files)}
              onFileFieldUpdated={onUpdateFileField}
              onRemoveFile={onRemoveFile}
              fetcher={fetcher}
            />
          )}
          <div className={styles.addFile}>
            <h4 className="margin-bottom-md margin-top-md">Add File(s):</h4>
            <DropZone onDrop={onDrop} accept={acceptFileTypes}>
              {
                ({ getRootProps, getInputProps }) => (
                  <div
                    className="clickable"
                    {...getRootProps()}
                    style={{
                      width: '100%',
                      border: 'dashed 2px #ccc',
                      padding: '50px',
                      borderRadius: '3px',
                      textAlign: 'center',
                    }}>
                    <span style={{ fontSize: '60px', color: '#aaa' }}>
                      <Icon icon={icons.fileO} />
                    </span><br /><br /><br /><p style={{ fontSize: '20px' }}>Drop files or click here to upload</p><i>{validFileExtensions.join(', ')}</i>
                    {fileType === FileType.SBOM && (
                      <p className={styles.sbomFormatHint}>
                        Supported formats: CycloneDX (JSON or XML), SPDX (JSON only)
                      </p>
                    )}
                    <input {...getInputProps()} />
                  </div>
                )
              }
            </DropZone>
          </div>
        </div>
      </div>
      <Modal show={state.showAssignToTeamsModal} onHide={teamAssignmentClosed}>
        <Modal.Header>
          <Modal.Title>
            Assign Project to Teams
          </Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <SelectTeam
            allTeams={allTeams}
            selectedTeams={state.assignToTeams}
            onTeamsSelected={(teams: Team[]) => onTeamsSelected(teams)}
            isRequired={isTeamRequired}
          />
        </Modal.Body>
        <Modal.Footer>
          <Button onClick={teamAssignmentClosed}>Cancel</Button>
          <Button
            onClick={onTeamsSelectionImport}
            bsStyle="success"
            disabled={(!userHasPermissionToViewAnyProject() || isTeamRequired) && state.assignToTeams.length === 0}
          >
            Import Projects
          </Button>
        </Modal.Footer>
      </Modal>
    </div>
  );
};

export default UploadComponentsPage;
